import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  browserNowOffsetDateTime,
  browserTimeZone,
  calendarYearEndExclusiveToApiZ,
  calendarYearStartToApiZ
} from '../core/date-utils';
import { DateTime } from 'luxon';
import type { ForecastKpis } from './forecast-cash-flow.service';

export type { ForecastKpis };

export interface ForecastBreakdownItem {
  key: string;
  amount: number;
  count: number;
}

export interface ForecastMonthCompareBucket {
  monthOfYear: number;
  label: string;
  yearACollected: number;
  yearACollectedCount: number;
  yearBCharges: number;
  yearBChargeCount: number;
  yearBSubscriptions: number;
  yearBSubscriptionCount: number;
  yearBCollected: number;
  yearBCollectedCount: number;
  yearBProjected: number;
  yearBExpected: number;
  yearATotal: number;
  yearBTotal: number;
  difference: number;
}

/** Cards ancladas al mes calendario actual / siguiente (no dependen de yearA/yearB). */
export interface ForecastSnapshotCards {
  currentMonthLabel: string;
  nextMonthLabel: string;
  currentMonthIncome: number;
  currentMonthExpected: number;
  currentMonthEventCount: number;
  currentMonthPending: number;
  currentMonthPendingCount: number;
  currentMonthOverdue: number;
  currentMonthOverdueCount: number;
  nextMonthProjection: number;
  nextMonthCharges: number;
  nextMonthSubscriptions: number;
  nextMonthEventCount: number;
}

export interface ForecastProductMonthBucket {
  year: number;
  monthOfYear: number;
  label: string;
  products: ForecastBreakdownItem[];
}

export interface ForecastCustomerCompareBucket {
  customerId: number;
  customerName: string;
  customerType: 'PERSON' | 'COMPANY' | '';
  rank: number;
  yearBAmount: number;
  yearBCount: number;
  yearAAmount: number;
  yearACount: number;
  difference: number;
  differencePercent: number;
}

export interface ForecastCustomerChargeItem {
  chargeId: number;
  description: string;
  total: number;
  paidAt: string | null;
  paymentNumber: number | null;
  numberOfPayments: number | null;
}

export interface ForecastCustomerSaleBucket {
  /** Clave estable para expandir filas en la tabla. */
  key: string;
  saleId: number | null;
  saleNumber: string | null;
  saleType: string | null;
  description: string;
  amount: number;
  chargeCount: number;
  firstPaidAt: string | null;
  lastPaidAt: string | null;
  charges: ForecastCustomerChargeItem[];
}

export interface ForecastCustomerCashFlowDetail {
  customerId: number;
  customerName: string;
  yearA: number;
  yearB: number;
  yearATotal: number;
  yearBTotal: number;
  yearAChargeCount: number;
  yearBChargeCount: number;
  yearASales: ForecastCustomerSaleBucket[];
  yearBSales: ForecastCustomerSaleBucket[];
  loading: boolean;
  error?: string;
}

export type ForecastSnapshotCardKind =
  | 'CURRENT_MONTH_INCOME'
  | 'CURRENT_MONTH_PENDING'
  | 'OVERDUE'
  | 'NEXT_MONTH_PROJECTION'
  | 'PRODUCT'
  | 'PAYMENT'
  | 'MONTH';

export interface ForecastSnapshotCardChargeItem {
  index: number;
  key: string;
  source: 'CHARGE' | 'SUBSCRIPTION' | string;
  chargeId: number | null;
  saleId: number | null;
  saleNumber: string | null;
  saleType: string | null;
  customerId: number | null;
  customerName: string;
  customerType: 'PERSON' | 'COMPANY' | '';
  description: string;
  paymentNumber: number | null;
  numberOfPayments: number | null;
  dueDate: string | null;
  paidAt: string | null;
  total: number;
  status: string | null;
  collectionMethod: string | null;
  subscriptionId: number | null;
}

export interface ForecastSnapshotCardDetail {
  card: ForecastSnapshotCardKind | string;
  title: string;
  subtitle: string;
  dateKind: 'PAID_AT' | 'DUE_DATE' | string;
  total: number;
  count: number;
  items: ForecastSnapshotCardChargeItem[];
  loading: boolean;
  error?: string;
}

export interface ForecastCashFlowV2Data {
  yearA: number;
  yearB: number;
  cards: ForecastSnapshotCards;
  kpis: ForecastKpis;
  months: ForecastMonthCompareBucket[];
  topCustomers: ForecastCustomerCompareBucket[];
  byPaymentMethod: ForecastBreakdownItem[];
  byProduct: ForecastBreakdownItem[];
  byProductByMonth: ForecastProductMonthBucket[];
  byPaymentMethodByMonth: ForecastProductMonthBucket[];
  byCollectionMethod: ForecastBreakdownItem[];
  loading: boolean;
  error?: string;
}

interface CashFlowMonthCompareApi {
  monthOfYear?: number;
  label?: string;
  yearACollected?: number;
  yearACollectedCount?: number;
  yearBCharges?: number;
  yearBChargeCount?: number;
  yearBSubscriptions?: number;
  yearBSubscriptionCount?: number;
  yearBCollected?: number;
  yearBCollectedCount?: number;
  yearBProjected?: number;
  yearBExpected?: number;
  yearATotal?: number;
  yearBTotal?: number;
  difference?: number;
}

interface SnapshotCardsApi {
  currentMonthLabel?: string;
  nextMonthLabel?: string;
  currentMonthIncome?: number;
  currentMonthExpected?: number;
  currentMonthEventCount?: number;
  currentMonthPending?: number;
  currentMonthPendingCount?: number;
  currentMonthOverdue?: number;
  currentMonthOverdueCount?: number;
  nextMonthProjection?: number;
  nextMonthCharges?: number;
  nextMonthSubscriptions?: number;
  nextMonthEventCount?: number;
}

interface CashFlowBreakdownApi {
  key?: string;
  amount?: number;
  count?: number;
}

interface ProductMonthBucketApi {
  year?: number;
  monthOfYear?: number;
  label?: string;
  products?: CashFlowBreakdownApi[];
}

interface CustomerCompareApi {
  customerId?: number;
  customerName?: string;
  customerType?: string;
  rank?: number;
  yearBAmount?: number;
  yearBCount?: number;
  yearAAmount?: number;
  yearACount?: number;
  difference?: number;
  differencePercent?: number;
}

interface CustomerChargeItemApi {
  chargeId?: number;
  description?: string;
  total?: number;
  paidAt?: string;
  paymentNumber?: number;
  numberOfPayments?: number;
}

interface CustomerSaleBucketApi {
  saleId?: number;
  saleNumber?: string;
  saleType?: string;
  description?: string;
  amount?: number;
  chargeCount?: number;
  firstPaidAt?: string;
  lastPaidAt?: string;
  charges?: CustomerChargeItemApi[];
}

interface CustomerCashFlowDetailApi {
  customerId?: number;
  customerName?: string;
  yearA?: number;
  yearB?: number;
  yearATotal?: number;
  yearBTotal?: number;
  yearAChargeCount?: number;
  yearBChargeCount?: number;
  yearASales?: CustomerSaleBucketApi[];
  yearBSales?: CustomerSaleBucketApi[];
}

interface WrapperCustomerCashFlowDetail {
  ok?: boolean;
  message?: string;
  body?: CustomerCashFlowDetailApi | null;
}

interface SnapshotCardChargeItemApi {
  key?: string;
  source?: string;
  chargeId?: number;
  saleId?: number;
  saleNumber?: string;
  saleType?: string;
  customerId?: number;
  customerName?: string;
  customerType?: string;
  description?: string;
  paymentNumber?: number;
  numberOfPayments?: number;
  dueDate?: string;
  paidAt?: string;
  total?: number;
  status?: string;
  collectionMethod?: string;
  subscriptionId?: number;
}

interface SnapshotCardDetailApi {
  card?: string;
  title?: string;
  subtitle?: string;
  dateKind?: string;
  total?: number;
  count?: number;
  items?: SnapshotCardChargeItemApi[];
}

interface WrapperSnapshotCardDetail {
  ok?: boolean;
  message?: string;
  body?: SnapshotCardDetailApi | null;
}

interface CashFlowForecastV2Api {
  yearA?: number;
  yearB?: number;
  cards?: SnapshotCardsApi;
  kpis?: Partial<ForecastKpis>;
  months?: CashFlowMonthCompareApi[];
  topCustomers?: CustomerCompareApi[];
  byPaymentMethod?: CashFlowBreakdownApi[];
  byProduct?: CashFlowBreakdownApi[];
  byProductByMonth?: ProductMonthBucketApi[];
  byPaymentMethodByMonth?: ProductMonthBucketApi[];
  byCollectionMethod?: CashFlowBreakdownApi[];
}

interface WrapperCashFlowForecastV2 {
  ok?: boolean;
  message?: string;
  body?: CashFlowForecastV2Api | null;
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function asCustomerType(value: string | undefined): 'PERSON' | 'COMPANY' | '' {
  return value === 'PERSON' || value === 'COMPANY' ? value : '';
}

function mapBreakdown(rows: CashFlowBreakdownApi[] | undefined): ForecastBreakdownItem[] {
  return (rows ?? []).map((r) => ({
    key: r.key ?? '—',
    amount: num(r.amount),
    count: num(r.count)
  }));
}

function emptyCards(): ForecastSnapshotCards {
  const now = DateTime.local();
  const next = now.plus({ months: 1 });
  const fmt = (d: DateTime) =>
    d.setLocale('es-MX').toFormat('MMMM');
  return {
    currentMonthLabel: fmt(now),
    nextMonthLabel: fmt(next),
    currentMonthIncome: 0,
    currentMonthExpected: 0,
    currentMonthEventCount: 0,
    currentMonthPending: 0,
    currentMonthPendingCount: 0,
    currentMonthOverdue: 0,
    currentMonthOverdueCount: 0,
    nextMonthProjection: 0,
    nextMonthCharges: 0,
    nextMonthSubscriptions: 0,
    nextMonthEventCount: 0
  };
}

function mapCards(c: SnapshotCardsApi | undefined): ForecastSnapshotCards {
  const fallback = emptyCards();
  if (!c) return fallback;
  return {
    currentMonthLabel: c.currentMonthLabel || fallback.currentMonthLabel,
    nextMonthLabel: c.nextMonthLabel || fallback.nextMonthLabel,
    currentMonthIncome: num(c.currentMonthIncome),
    currentMonthExpected: num(c.currentMonthExpected),
    currentMonthEventCount: num(c.currentMonthEventCount),
    currentMonthPending: num(c.currentMonthPending),
    currentMonthPendingCount: num(c.currentMonthPendingCount),
    currentMonthOverdue: num(c.currentMonthOverdue),
    currentMonthOverdueCount: num(c.currentMonthOverdueCount),
    nextMonthProjection: num(c.nextMonthProjection),
    nextMonthCharges: num(c.nextMonthCharges),
    nextMonthSubscriptions: num(c.nextMonthSubscriptions),
    nextMonthEventCount: num(c.nextMonthEventCount)
  };
}

const MONTH_LABELS = [
  'ene', 'feb', 'mar', 'abr', 'may', 'jun',
  'jul', 'ago', 'sep', 'oct', 'nov', 'dic'
];

@Injectable({ providedIn: 'root' })
export class ForecastCashFlowV2Service {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  loadForecast(yearA: number, yearB: number): Observable<ForecastCashFlowV2Data> {
    const params = this.buildYearRangeParams(yearA, yearB);

    return this.http
      .get<WrapperCashFlowForecastV2>(`${this.apiUrl}/forecasts/cash-flow-v2`, { params })
      .pipe(
        map((res) => {
          if (!res?.ok || !res.body) {
            return this.emptyForecast(yearA, yearB, res?.message || 'No se pudo cargar el forecast');
          }
          return this.mapApi(res.body, yearA, yearB);
        }),
        catchError((err) =>
          of(this.emptyForecast(yearA, yearB, err?.error?.message || err?.message || 'Error al cargar el forecast'))
        )
      );
  }

  loadCustomerDetail(
    customerId: number,
    yearA: number,
    yearB: number
  ): Observable<ForecastCustomerCashFlowDetail> {
    const params = this.buildYearRangeParams(yearA, yearB);

    return this.http
      .get<WrapperCustomerCashFlowDetail>(
        `${this.apiUrl}/forecasts/cash-flow-v2/customers/${customerId}`,
        { params }
      )
      .pipe(
        map((res) => {
          if (!res?.ok || !res.body) {
            return this.emptyCustomerDetail(
              customerId,
              yearA,
              yearB,
              res?.message || 'No se pudo cargar el desglose'
            );
          }
          return this.mapCustomerDetail(res.body, customerId, yearA, yearB);
        }),
        catchError((err) =>
          of(
            this.emptyCustomerDetail(
              customerId,
              yearA,
              yearB,
              err?.error?.message || err?.message || 'Error al cargar el desglose'
            )
          )
        )
      );
  }

  loadCardDetail(
    card: ForecastSnapshotCardKind,
    opts?: { year?: number; month?: number }
  ): Observable<ForecastSnapshotCardDetail> {
    let params = new HttpParams()
      .set('timeZone', browserTimeZone())
      .set('clientNow', browserNowOffsetDateTime());
    if (opts?.year != null) {
      params = params.set('year', String(opts.year));
    }
    if (opts?.month != null) {
      params = params.set('month', String(opts.month));
    }
    return this.http
      .get<WrapperSnapshotCardDetail>(`${this.apiUrl}/forecasts/cash-flow-v2/cards/${card}`, { params })
      .pipe(
        map((res) => {
          if (!res?.ok || !res.body) {
            return this.emptyCardDetail(card, res?.message || 'No se pudo cargar el desglose');
          }
          return this.mapCardDetail(res.body, card);
        }),
        catchError((err) =>
          of(this.emptyCardDetail(card, err?.error?.message || err?.message || 'Error al cargar el desglose'))
        )
      );
  }

  loadProductDetail(
    year: number,
    month: number,
    product: string
  ): Observable<ForecastSnapshotCardDetail> {
    const card = 'PRODUCT';
    const params = new HttpParams()
      .set('timeZone', browserTimeZone())
      .set('clientNow', browserNowOffsetDateTime())
      .set('year', String(year))
      .set('month', String(month))
      .set('product', product || 'Sin producto');
    return this.http
      .get<WrapperSnapshotCardDetail>(`${this.apiUrl}/forecasts/cash-flow-v2/products`, { params })
      .pipe(
        map((res) => {
          if (!res?.ok || !res.body) {
            return this.emptyCardDetail(card, res?.message || 'No se pudo cargar el desglose');
          }
          return this.mapCardDetail(res.body, card);
        }),
        catchError((err) =>
          of(this.emptyCardDetail(card, err?.error?.message || err?.message || 'Error al cargar el desglose'))
        )
      );
  }

  loadPaymentDetail(
    year: number,
    month: number,
    payment: string
  ): Observable<ForecastSnapshotCardDetail> {
    const card = 'PAYMENT';
    const params = new HttpParams()
      .set('timeZone', browserTimeZone())
      .set('clientNow', browserNowOffsetDateTime())
      .set('year', String(year))
      .set('month', String(month))
      .set('payment', payment || '—');
    return this.http
      .get<WrapperSnapshotCardDetail>(`${this.apiUrl}/forecasts/cash-flow-v2/payment-methods`, { params })
      .pipe(
        map((res) => {
          if (!res?.ok || !res.body) {
            return this.emptyCardDetail(card, res?.message || 'No se pudo cargar el desglose');
          }
          return this.mapCardDetail(res.body, card);
        }),
        catchError((err) =>
          of(this.emptyCardDetail(card, err?.error?.message || err?.message || 'Error al cargar el desglose'))
        )
      );
  }

  loadMonthDetail(year: number, month: number): Observable<ForecastSnapshotCardDetail> {
    const card = 'MONTH';
    const params = new HttpParams()
      .set('timeZone', browserTimeZone())
      .set('clientNow', browserNowOffsetDateTime())
      .set('year', String(year))
      .set('month', String(month));
    return this.http
      .get<WrapperSnapshotCardDetail>(`${this.apiUrl}/forecasts/cash-flow-v2/months`, { params })
      .pipe(
        map((res) => {
          if (!res?.ok || !res.body) {
            return this.emptyCardDetail(card, res?.message || 'No se pudo cargar el desglose');
          }
          return this.mapCardDetail(res.body, card);
        }),
        catchError((err) =>
          of(this.emptyCardDetail(card, err?.error?.message || err?.message || 'Error al cargar el desglose'))
        )
      );
  }

  private buildYearRangeParams(yearA: number, yearB: number): HttpParams {
    return new HttpParams()
      .set('from', calendarYearStartToApiZ(yearA))
      .set('to', calendarYearEndExclusiveToApiZ(yearB))
      .set('timeZone', browserTimeZone())
      .set('clientNow', browserNowOffsetDateTime());
  }

  private mapSaleBucket(s: CustomerSaleBucketApi): ForecastCustomerSaleBucket {
    const saleId = s.saleId != null ? num(s.saleId) : null;
    const charges = (s.charges ?? []).map((c) => ({
      chargeId: num(c.chargeId),
      description: c.description || '—',
      total: num(c.total),
      paidAt: c.paidAt ?? null,
      paymentNumber: c.paymentNumber != null ? num(c.paymentNumber) : null,
      numberOfPayments: c.numberOfPayments != null ? num(c.numberOfPayments) : null
    }));
    const orphanChargeId = charges[0]?.chargeId;
    return {
      key: saleId != null ? `S:${saleId}` : `C:${orphanChargeId ?? Math.random()}`,
      saleId,
      saleNumber: s.saleNumber ?? null,
      saleType: s.saleType ?? null,
      description: s.description || 'Sin descripción',
      amount: num(s.amount),
      chargeCount: num(s.chargeCount),
      firstPaidAt: s.firstPaidAt ?? null,
      lastPaidAt: s.lastPaidAt ?? null,
      charges
    };
  }

  private mapCustomerDetail(
    body: CustomerCashFlowDetailApi,
    customerId: number,
    yearA: number,
    yearB: number
  ): ForecastCustomerCashFlowDetail {
    return {
      customerId: body.customerId ?? customerId,
      customerName: body.customerName || 'Sin cliente',
      yearA: body.yearA ?? yearA,
      yearB: body.yearB ?? yearB,
      yearATotal: num(body.yearATotal),
      yearBTotal: num(body.yearBTotal),
      yearAChargeCount: num(body.yearAChargeCount),
      yearBChargeCount: num(body.yearBChargeCount),
      yearASales: (body.yearASales ?? []).map((s) => this.mapSaleBucket(s)),
      yearBSales: (body.yearBSales ?? []).map((s) => this.mapSaleBucket(s)),
      loading: false
    };
  }

  private mapCardDetail(
    body: SnapshotCardDetailApi,
    card: ForecastSnapshotCardKind
  ): ForecastSnapshotCardDetail {
    const items: ForecastSnapshotCardChargeItem[] = (body.items ?? []).map((c, i) => {
      const chargeId = c.chargeId != null ? num(c.chargeId) : null;
      const subscriptionId = c.subscriptionId != null ? num(c.subscriptionId) : null;
      const source =
        c.source === 'SUBSCRIPTION' ? 'SUBSCRIPTION' : c.source === 'SALE' ? 'SALE' : 'CHARGE';
      return {
        index: i + 1,
        key: c.key || (chargeId != null ? `C:${chargeId}` : `S:${subscriptionId ?? i}`),
        source,
        chargeId,
        saleId: c.saleId != null ? num(c.saleId) : null,
        saleNumber: c.saleNumber ?? null,
        saleType: c.saleType ?? null,
        customerId: c.customerId != null ? num(c.customerId) : null,
        customerName: c.customerName || 'Sin cliente',
        customerType: asCustomerType(c.customerType),
        description: c.description || '—',
        paymentNumber: c.paymentNumber != null ? num(c.paymentNumber) : null,
        numberOfPayments: c.numberOfPayments != null ? num(c.numberOfPayments) : null,
        dueDate: c.dueDate ?? null,
        paidAt: c.paidAt ?? null,
        total: num(c.total),
        status: c.status ?? null,
        collectionMethod: c.collectionMethod ?? null,
        subscriptionId
      };
    });
    return {
      card: body.card || card,
      title: body.title || 'Desglose',
      subtitle: body.subtitle || 'pagos',
      dateKind: body.dateKind || 'DUE_DATE',
      total: num(body.total),
      count: num(body.count) || items.length,
      items,
      loading: false
    };
  }

  private emptyCardDetail(card: ForecastSnapshotCardKind, error?: string): ForecastSnapshotCardDetail {
    return {
      card,
      title: 'Desglose',
      subtitle: 'pagos',
      dateKind: 'DUE_DATE',
      total: 0,
      count: 0,
      items: [],
      loading: false,
      error
    };
  }

  private emptyCustomerDetail(
    customerId: number,
    yearA: number,
    yearB: number,
    error?: string
  ): ForecastCustomerCashFlowDetail {
    return {
      customerId,
      customerName: 'Sin cliente',
      yearA,
      yearB,
      yearATotal: 0,
      yearBTotal: 0,
      yearAChargeCount: 0,
      yearBChargeCount: 0,
      yearASales: [],
      yearBSales: [],
      loading: false,
      error
    };
  }

  private mapApi(body: CashFlowForecastV2Api, yearA: number, yearB: number): ForecastCashFlowV2Data {
    const k = body.kpis ?? {};
    const byCollectionMethod = mapBreakdown(body.byCollectionMethod);
    const byPaymentMethod = mapBreakdown(body.byPaymentMethod);
    return {
      yearA: body.yearA ?? yearA,
      yearB: body.yearB ?? yearB,
      cards: mapCards(body.cards),
      kpis: {
        totalProjected: num(k.totalProjected),
        totalExpected: num(k.totalExpected),
        chargesProjected: num(k.chargesProjected),
        subscriptionsProjected: num(k.subscriptionsProjected),
        mrr: num(k.mrr),
        atRiskAmount: num(k.atRiskAmount),
        overdueAmount: num(k.overdueAmount),
        collectedInHorizon: num(k.collectedInHorizon),
        activeSubscriptions: num(k.activeSubscriptions),
        eventCount: num(k.eventCount)
      },
      months: (body.months ?? []).map((m) => {
        const monthOfYear = num(m.monthOfYear) || 1;
        return {
          monthOfYear,
          label: m.label || MONTH_LABELS[monthOfYear - 1] || String(monthOfYear),
          yearACollected: num(m.yearACollected),
          yearACollectedCount: num(m.yearACollectedCount),
          yearBCharges: num(m.yearBCharges),
          yearBChargeCount: num(m.yearBChargeCount),
          yearBSubscriptions: num(m.yearBSubscriptions),
          yearBSubscriptionCount: num(m.yearBSubscriptionCount),
          yearBCollected: num(m.yearBCollected),
          yearBCollectedCount: num(m.yearBCollectedCount),
          yearBProjected: num(m.yearBProjected),
          yearBExpected: num(m.yearBExpected),
          yearATotal: num(m.yearATotal),
          yearBTotal: num(m.yearBTotal),
          difference: num(m.difference)
        };
      }),
      byCollectionMethod,
      byPaymentMethod: byPaymentMethod.length > 0 ? byPaymentMethod : byCollectionMethod,
      byProduct: mapBreakdown(body.byProduct),
      byProductByMonth: (body.byProductByMonth ?? []).map((m) => {
        const monthOfYear = num(m.monthOfYear) || 1;
        return {
          year: num(m.year),
          monthOfYear,
          label: m.label || MONTH_LABELS[monthOfYear - 1] || String(monthOfYear),
          products: mapBreakdown(m.products)
        };
      }),
      byPaymentMethodByMonth: (body.byPaymentMethodByMonth ?? []).map((m) => {
        const monthOfYear = num(m.monthOfYear) || 1;
        return {
          year: num(m.year),
          monthOfYear,
          label: m.label || MONTH_LABELS[monthOfYear - 1] || String(monthOfYear),
          products: mapBreakdown(m.products)
        };
      }),
      topCustomers: (body.topCustomers ?? []).map((c) => ({
        customerId: num(c.customerId),
        customerName: c.customerName || 'Sin cliente',
        customerType:
          c.customerType === 'PERSON' || c.customerType === 'COMPANY' ? c.customerType : ('' as const),
        rank: num(c.rank),
        yearBAmount: num(c.yearBAmount),
        yearBCount: num(c.yearBCount),
        yearAAmount: num(c.yearAAmount),
        yearACount: num(c.yearACount),
        difference: num(c.difference),
        differencePercent: num(c.differencePercent)
      })),
      loading: false
    };
  }

  private emptyForecast(yearA: number, yearB: number, error?: string): ForecastCashFlowV2Data {
    return {
      yearA,
      yearB,
      cards: emptyCards(),
      kpis: {
        totalProjected: 0,
        totalExpected: 0,
        chargesProjected: 0,
        subscriptionsProjected: 0,
        mrr: 0,
        atRiskAmount: 0,
        overdueAmount: 0,
        collectedInHorizon: 0,
        activeSubscriptions: 0,
        eventCount: 0
      },
      months: MONTH_LABELS.map((label, i) => ({
        monthOfYear: i + 1,
        label,
        yearACollected: 0,
        yearACollectedCount: 0,
        yearBCharges: 0,
        yearBChargeCount: 0,
        yearBSubscriptions: 0,
        yearBSubscriptionCount: 0,
        yearBCollected: 0,
        yearBCollectedCount: 0,
        yearBProjected: 0,
        yearBExpected: 0,
        yearATotal: 0,
        yearBTotal: 0,
        difference: 0
      })),
      byCollectionMethod: [],
      byPaymentMethod: [],
      byProduct: [],
      byProductByMonth: [yearA, yearB].flatMap((year) =>
        MONTH_LABELS.map((label, i) => ({
          year,
          monthOfYear: i + 1,
          label,
          products: [] as ForecastBreakdownItem[]
        }))
      ),
      byPaymentMethodByMonth: [yearA, yearB].flatMap((year) =>
        MONTH_LABELS.map((label, i) => ({
          year,
          monthOfYear: i + 1,
          label,
          products: [] as ForecastBreakdownItem[]
        }))
      ),
      topCustomers: [],
      loading: false,
      error
    };
  }
}
